import React, { useEffect, useState, useCallback } from 'react';
import { supabase, uploadImage, deleteImageByUrl } from '../../services/supabaseClient';
import Modal from '../Modal';
import ImageUploader from '../ImageUploader';

// Ediciones de la revista. Alimentan el carrusel de portadas del sitio público.
const RevistasSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    numero: '',
    titulo: '',
    enlace: '',
    image_url: '',
    visible: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('revistas')
      .select('*')
      .order('numero', { ascending: false });
    if (error) {
      // Antes esto solo iba a consola y la tabla mostraba "sin registros": el
      // mismo aspecto que tener la tabla vacía de verdad. Imposible distinguir
      // un fallo de permisos de una lista vacía.
      console.error(error);
      setLoadError(error.message);
      setItems([]);
    } else {
      setLoadError('');
      setItems(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const siguienteNumero = () => {
    if (items.length === 0) return 1;
    return Math.max(...items.map((i) => i.numero || 0)) + 1;
  };

  const openCreateModal = () => {
    const numero = siguienteNumero();
    setEditing(null);
    setFormData({
      numero,
      titulo: `Revista ${numero}`,
      enlace: '',
      image_url: '',
      visible: true,
    });
    setImageFile(null);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditing(item);
    setFormData({
      numero: item.numero ?? '',
      titulo: item.titulo || '',
      enlace: item.enlace || '',
      image_url: item.image_url || '',
      visible: item.visible !== false,
    });
    setImageFile(null);
    setFormError('');
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar la edición "${item.titulo}"?`)) return;
    const { error } = await supabase.from('revistas').delete().eq('id', item.id);
    if (error) {
      alert('Error: ' + error.message);
      return;
    }
    if (item.image_url) await deleteImageByUrl(item.image_url);
    fetchData();
  };

  // Ocultar sin borrar: la edición desaparece del carrusel pero se conserva.
  const toggleVisible = async (item) => {
    const { error } = await supabase
      .from('revistas')
      .update({ visible: !item.visible, updated_at: new Date().toISOString() })
      .eq('id', item.id);
    if (error) alert('Error: ' + error.message);
    else fetchData();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      let imageUrl = formData.image_url;
      if (imageFile) {
        if (editing && editing.image_url) await deleteImageByUrl(editing.image_url);
        imageUrl = await uploadImage(imageFile);
      } else if (imageFile === null && formData.image_url === '') {
        if (editing && editing.image_url) await deleteImageByUrl(editing.image_url);
        imageUrl = null;
      }

      if (!imageUrl) throw new Error('La portada es obligatoria');
      if (!formData.numero) throw new Error('El número de edición es obligatorio');

      const payload = {
        numero: Number(formData.numero),
        titulo: formData.titulo.trim(),
        enlace: formData.enlace.trim() || null,
        image_url: imageUrl,
        visible: formData.visible,
        updated_at: new Date().toISOString(),
      };

      if (editing) {
        const { error } = await supabase.from('revistas').update(payload).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('revistas').insert([payload]);
        if (error) {
          // El número es único: avisar en lenguaje claro en vez del error crudo.
          if (error.code === '23505') {
            throw new Error(`Ya existe una edición con el número ${formData.numero}.`);
          }
          throw error;
        }
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="add-button-container">
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Agregar Edición
        </button>
      </div>

      {loadError && (
        <div className="error-message">
          No se pudieron cargar los datos: {loadError}
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Acciones</th>
                <th>Portada</th>
                <th>N.º</th>
                <th>Título</th>
                <th>Enlace</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-row">
                    Sin ediciones registradas
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="actions-cell">
                        <button className="btn btn-secondary btn-sm" onClick={() => openEditModal(item)}>
                          Editar
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => toggleVisible(item)}>
                          {item.visible ? 'Ocultar' : 'Mostrar'}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item)}>
                          Eliminar
                        </button>
                      </div>
                    </td>
                    <td>
                      {item.image_url ? (
                        <img src={item.image_url} alt="" className="table-image" loading="lazy" decoding="async" />
                      ) : (
                        <div className="no-image">Sin portada</div>
                      )}
                    </td>
                    <td>{item.numero}</td>
                    <td>{item.titulo}</td>
                    <td>
                      {item.enlace ? (
                        <a href={item.enlace} target="_blank" rel="noreferrer" title={item.enlace}>
                          Ver enlace
                        </a>
                      ) : (
                        <span style={{ color: '#999' }}>Sin enlace</span>
                      )}
                    </td>
                    <td>
                      <span style={{ color: item.visible ? '#2d6a2d' : '#999' }}>
                        {item.visible ? 'Visible' : 'Oculta'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal
          title={editing ? 'Editar Edición' : 'Agregar Edición'}
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </>
          }
        >
          <form onSubmit={handleSubmit}>
            {formError && <div className="error-message">{formError}</div>}

            <div className="form-row">
              <div className="form-group">
                <label>Número de edición *</label>
                <input
                  type="number"
                  value={formData.numero}
                  onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                  required
                />
                <small style={{ color: '#666', fontSize: '0.85em' }}>
                  Ordena el carrusel: el más alto aparece primero.
                </small>
              </div>

              <div className="form-group">
                <label>Título *</label>
                <input
                  type="text"
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Enlace</label>
              <input
                type="url"
                value={formData.enlace}
                onChange={(e) => setFormData({ ...formData, enlace: e.target.value })}
                placeholder="https://www.camara-arequipa.org.pe/.../revista.pdf"
              />
              <small style={{ color: '#666', fontSize: '0.85em' }}>
                Dirección web donde está alojado el PDF de la edición. No se sube
                el archivo aquí: se pega el enlace y el sitio lleva a él al pulsar
                la portada.
              </small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={formData.visible}
                  onChange={(e) => setFormData({ ...formData, visible: e.target.checked })}
                  style={{ width: 'auto' }}
                />
                Mostrar en el sitio
              </label>
            </div>

            <ImageUploader
              currentImageUrl={formData.image_url}
              onFileSelect={(file) => {
                setImageFile(file);
                if (file === null) setFormData({ ...formData, image_url: '' });
              }}
              label="Portada *"
            />
          </form>
        </Modal>
      )}
    </div>
  );
};

export default RevistasSection;
